import React, { useState, useEffect } from 'react'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'
import { API_BASE, getImageUrl } from '../../utils/api'

interface HeroBanner {
    _id: string;
    type: 'image' | 'video';
    url: string;
    title?: string;
    subtitle?: string;
    link?: string;
    isActive: boolean;
    order: number;
    createdAt: string;
}

export default function AdminHeroBanners() {
    const { token } = useAuth()
    const [banners, setBanners] = useState<HeroBanner[]>([])
    const [loading, setLoading] = useState(true)
    const [formType, setFormType] = useState<'image' | 'video'>('image')
    const [formUrl, setFormUrl] = useState('')
    const [formTitle, setFormTitle] = useState('')
    const [formSubtitle, setFormSubtitle] = useState('')
    const [formLink, setFormLink] = useState('')
    const [formOrder, setFormOrder] = useState(0)
    const [editId, setEditId] = useState<string | null>(null)
    const [submitting, setSubmitting] = useState(false)
    const [msg, setMsg] = useState('')
    const [uploading, setUploading] = useState(false)
    const [isModalOpen, setIsModalOpen] = useState(false)

    const fetchBanners = () => {
        setLoading(true)
        fetch(`${API_BASE}/hero-banners/admin`, {
            headers: { 'Authorization': `Bearer ${token}` }
        })
            .then(r => r.json())
            .then(data => { if (Array.isArray(data)) setBanners(data) })
            .catch(console.error)
            .finally(() => setLoading(false))
    }

    useEffect(() => { fetchBanners() }, [])

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        setUploading(true)
        const fd = new FormData()
        
        // Choose endpoint and field name based on formType
        const endpoint = formType === 'video' ? 'video' : 'nearby-image'
        const fieldName = formType === 'video' ? 'video' : 'image'
        
        fd.append(fieldName, file)
        
        try {
            const res = await fetch(`${API_BASE}/upload/${endpoint}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            })
            const data = await res.json()
            if (data.url) {
                setFormUrl(data.url)
            } else {
                setMsg(data.message || 'Upload failed')
            }
        } catch (err) { 
            console.error(err)
            setMsg('Server error during upload')
        } finally { 
            setUploading(false) 
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!formUrl.trim()) { setMsg('File URL or Upload is required'); return }
        setSubmitting(true); setMsg('')
        try {
            const url = editId ? `${API_BASE}/hero-banners/${editId}` : `${API_BASE}/hero-banners`
            const method = editId ? 'PUT' : 'POST'
            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ 
                    type: formType, 
                    url: formUrl.trim(), 
                    title: formTitle,
                    subtitle: formSubtitle,
                    link: formLink,
                    order: formOrder
                })
            })
            const data = await res.json()
            if (res.ok) {
                resetForm()
                setMsg(editId ? 'Banner updated!' : 'Banner added!')
                fetchBanners()
                setTimeout(() => setMsg(''), 3000)
            } else {
                setMsg(data.message || 'Error')
            }
        } catch { setMsg('Server error') }
        finally { setSubmitting(false) }
    }

    const resetForm = () => {
        setEditId(null)
        setFormType('image')
        setFormUrl('')
        setFormTitle('')
        setFormSubtitle('')
        setFormLink('')
        setFormOrder(0)
        setIsModalOpen(false)
    }

    const handleEdit = (banner: HeroBanner) => {
        setEditId(banner._id)
        setFormType(banner.type)
        setFormUrl(banner.url)
        setFormTitle(banner.title || '')
        setFormSubtitle(banner.subtitle || '')
        setFormLink(banner.link || '')
        setFormOrder(banner.order)
        setIsModalOpen(true)
    }

    const handleDelete = async (id: string) => {
        if (!window.confirm('Delete this banner?')) return
        try {
            await fetch(`${API_BASE}/hero-banners/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            })
            fetchBanners()
        } catch { }
    }

    const handleToggleActive = async (banner: HeroBanner) => {
        try {
            await fetch(`${API_BASE}/hero-banners/${banner._id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ isActive: !banner.isActive })
            })
            fetchBanners()
        } catch { }
    }

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-black mb-2 tracking-tight">Hero Banners</h1>
                    <p className="text-black text-sm font-bold">Manage high-impact visual content for your storefront's main stage.</p>
                </div>
                <button
                    onClick={() => { resetForm(); setIsModalOpen(true) }}
                    className="flex items-center gap-2 px-8 py-4 bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 rounded-md font-bold text-[14px] transition-all shadow-md active:scale-95 flex items-center gap-3"
                >
                    <i className="fa-solid fa-plus text-[12px]"></i>
                    Add New Banner
                </button>
            </div>

                <div className={`mb-6 p-4 rounded-md flex items-center gap-3 animate-in fade-in slide-in-from-top-4 ${
                    msg.includes('error') || msg.includes('required')
                    ? 'bg-rose-50 text-rose-700 border border-rose-100' 
                    : 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                }`}>
                    <i className={`fa-solid ${msg.includes('error') ? 'fa-triangle-exclamation' : 'fa-circle-check'}`}></i>
                    <p className="text-sm font-bold">{msg}</p>
                </div>

            <div className="w-full mb-12">
                {/* ── Modal Dialog for Form ── */}
                {isModalOpen && (
                    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[99999] flex justify-center items-center p-4 md:p-8 overflow-hidden" onClick={resetForm}>
                        <div className="bg-white dark:bg-slate-900 rounded-[32px] border border-blue-900/10 dark:border-slate-800 shadow-2xl w-full max-w-2xl max-h-full flex flex-col relative animate-in zoom-in-95 fade-in duration-200" onClick={e => e.stopPropagation()}>
                            
                            {/* Modal Header */}
                            <div className="relative h-28 bg-[#05144b] flex flex-col justify-end p-6 shrink-0">
                                <button 
                                    onClick={resetForm}
                                    className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 text-white hover:bg-white/40 transition-colors flex items-center justify-center"
                                >
                                    <i className="fa-solid fa-xmark text-sm"></i>
                                </button>
                                <div className="flex items-center gap-4">
                                    <div className={`w-14 h-14 rounded-md bg-white p-1 shadow-2xl overflow-hidden`}>
                                        <div className={`w-full h-full rounded-[4px] flex items-center justify-center text-[#05144b] bg-white`}>
                                            <i className={`fa-solid ${editId ? 'fa-pen-nib' : 'fa-plus'} text-xl`}></i>
                                        </div>
                                    </div>
                                    <div className="mb-1">
                                        <h2 className="text-lg font-bold text-white tracking-tight">{editId ? 'Modify Creative' : 'New Campaign Stage'}</h2>
                                        <p className="text-white/60 text-[10px] font-bold tracking-widest uppercase">Visual Asset Configuration</p>
                                    </div>
                                </div>
                            </div>

                            {/* Modal Body */}
                            <div className="overflow-y-auto custom-scrollbar flex-1">
                                {formUrl && (
                                    <div className="p-8 pb-0 animate-in fade-in zoom-in-95">
                                        <label className="block text-[11px] font-bold text-black uppercase mb-3">Live Preview</label>
                                        <div className="relative aspect-video rounded-md overflow-hidden bg-gray-50 border border-gray-100 group shadow-sm">
                                            {formType === 'image' ? (
                                                <img src={getImageUrl(formUrl)} alt="preview" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                                            ) : (
                                                <video src={getImageUrl(formUrl)} controls className="w-full h-full object-cover" />
                                            )}
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex flex-col justify-end p-6">
                                                <p className="text-white font-bold text-lg tracking-tight">{formTitle || 'Banner Title'}</p>
                                                <p className="text-white/80 text-xs font-bold">{formSubtitle || 'Supporting tagline text goes here'}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <form onSubmit={handleSubmit} className="p-6 px-8">
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-[11px] font-bold text-black uppercase mb-1.5">Content Type</label>
                                            <div className="grid grid-cols-2 gap-3 p-1.5 bg-gray-50 rounded-md">
                                                <button
                                                    type="button"
                                                    onClick={() => setFormType('image')}
                                                    className={`py-2 px-4 rounded-md text-[11px] font-bold uppercase transition-all ${formType === 'image' ? 'bg-[#05144b] text-white shadow-lg' : 'text-gray-400 hover:text-black'}`}
                                                >
                                                    <i className="fa-solid fa-image mr-2"></i>Image
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setFormType('video')}
                                                    className={`py-2 px-4 rounded-md text-[11px] font-bold uppercase transition-all ${formType === 'video' ? 'bg-[#05144b] text-white shadow-lg' : 'text-gray-400 hover:text-black'}`}
                                                >
                                                    <i className="fa-solid fa-film mr-2"></i>Video
                                                </button>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-[11px] font-bold text-black uppercase mb-1.5">Banner Asset</label>
                                            <div className="space-y-4">
                                                <div className="group relative">
                                                    <div className={`p-6 rounded-md border-2 border-dashed transition-all flex flex-col items-center justify-center gap-2 ${uploading ? 'border-[#05144b] bg-gray-50' : 'border-gray-100 hover:border-[#05144b] hover:bg-gray-50 shadow-sm'}`}>
                                                        <i className={`fa-solid ${formType === 'image' ? 'fa-cloud-arrow-up' : 'fa-file-video'} text-2xl text-gray-200 group-hover:text-[#05144b] transition-colors`}></i>
                                                        <div className="text-center">
                                                            <p className="text-[11px] font-bold text-black uppercase">Click to Upload {formType}</p>
                                                            <p className="text-[10px] text-gray-400 mt-0.5 font-bold">Max Payload: 20MB</p>
                                                        </div>
                                                        <input 
                                                            type="file" 
                                                            className="absolute inset-0 opacity-0 cursor-pointer" 
                                                            onChange={handleFileUpload} 
                                                            accept={formType === 'image' ? 'image/*' : 'video/*'} 
                                                            disabled={uploading}
                                                        />
                                                        {uploading && (
                                                            <div className="absolute inset-0 bg-white/80 dark:bg-slate-900/80 rounded-2xl flex items-center justify-center backdrop-blur-[2px]">
                                                                <div className="w-8 h-8 border-4 border-[#05144b] border-t-transparent rounded-full animate-spin"></div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                                        <i className="fa-solid fa-link text-gray-300 text-[12px]"></i>
                                                    </div>
                                                    <input 
                                                        type="text" 
                                                        className="w-full pl-10 pr-4 py-3 rounded-md bg-white border border-gray-100 focus:ring-2 focus:ring-[#05144b] text-black transition-all placeholder:text-gray-300 text-[14px] font-bold shadow-sm" 
                                                        placeholder="Alternative Manual URL" 
                                                        value={formUrl} 
                                                        onChange={e => setFormUrl(e.target.value)} 
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                                            <div className="col-span-2">
                                                <label className="block text-[11px] font-bold text-black uppercase mb-1.5">Creative Headline</label>
                                                <input 
                                                    type="text" 
                                                    className="w-full px-5 py-3 rounded-md bg-white border border-gray-100 focus:ring-2 focus:ring-[#05144b] text-black transition-all placeholder:text-gray-300 text-[14px] font-bold shadow-sm" 
                                                    placeholder="Catchy Main Title"
                                                    value={formTitle} 
                                                    onChange={e => setFormTitle(e.target.value)} 
                                                />
                                            </div>
                                            <div className="col-span-2 text-black">
                                                <label className="block text-[11px] font-bold text-black uppercase mb-1.5">Sub-Headline Text</label>
                                                <input 
                                                    type="text" 
                                                    className="w-full px-5 py-3 rounded-md bg-white border border-gray-100 focus:ring-2 focus:ring-[#05144b] text-black transition-all placeholder:text-gray-300 text-[14px] font-bold shadow-sm" 
                                                    placeholder="Supporting Taglines"
                                                    value={formSubtitle} 
                                                    onChange={e => setFormSubtitle(e.target.value)} 
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[11px] font-bold text-black uppercase mb-1.5">Destination Link</label>
                                                <input 
                                                    type="text" 
                                                    className="w-full px-5 py-3 rounded-md bg-white border border-gray-100 focus:ring-2 focus:ring-[#05144b] text-black transition-all placeholder:text-gray-300 text-[14px] font-bold shadow-sm" 
                                                    placeholder="/properties"
                                                    value={formLink} 
                                                    onChange={e => setFormLink(e.target.value)} 
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[11px] font-bold text-black uppercase mb-1.5">Display Index</label>
                                                <input 
                                                    type="number" 
                                                    className="w-full px-5 py-3 rounded-md bg-white border border-gray-100 focus:ring-2 focus:ring-[#05144b] text-black transition-all placeholder:text-gray-300 text-[14px] font-bold shadow-sm" 
                                                    value={formOrder} 
                                                    onChange={e => setFormOrder(parseInt(e.target.value))} 
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <button 
                                        type="submit" 
                                        className={`w-full py-4 rounded-md font-bold text-[14px] transition-all transform active:scale-[0.98] shadow-md flex items-center justify-center gap-3 bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 mt-6`}
                                        disabled={submitting || uploading}
                                    >
                                        {submitting ? (
                                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        ) : (
                                            <>
                                                <i className={`fa-solid ${editId ? 'fa-floppy-disk' : 'fa-rocket'} text-[12px]`}></i>
                                                <span>{editId ? 'Update Creative' : 'Deploy Banner'}</span>
                                            </>
                                        )}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* Table Section (Now Full Width) */}
                <div className="w-full mt-8">
                    <div className="bg-white dark:bg-slate-900 rounded-[32px] border border-blue-900/10 dark:border-slate-800 shadow-xl shadow-slate-200/50 dark:shadow-none overflow-hidden">
                        <div className="overflow-x-auto">
                            {loading ? (
                                <div className="p-20 text-center">
                                <div className="animate-spin w-10 h-10 border-4 border-[#05144b] border-t-transparent rounded-full mx-auto mb-4"></div>
                                <p className="text-black font-bold text-[14px]">Syncing Creative Assets...</p>
                                </div>
                            ) : banners.length === 0 ? (
                                <div className="p-20 text-center">
                                    <div className="w-20 h-20 bg-white dark:bg-slate-800/50 rounded-full flex items-center justify-center mx-auto mb-4 text-black/10 text-3xl">
                                        <i className="fa-solid fa-images"></i>
                                    </div>
                                    <h3 className="text-lg font-bold text-black tracking-tight">Active Stage is Empty</h3>
                                    <p className="text-black text-sm mt-1 font-bold">Start by adding your first visual highlight</p>
                                </div>
                            ) : (
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="bg-gray-50 border-b border-gray-100">
                                            <th className="px-6 py-4 text-[12px] font-bold text-black uppercase">Asset Preview</th>
                                            <th className="px-6 py-4 text-[12px] font-bold text-black uppercase">Campaign Info</th>
                                            <th className="px-6 py-4 text-[12px] font-bold text-black uppercase text-center">Index</th>
                                            <th className="px-6 py-4 text-[12px] font-bold text-black uppercase">Status</th>
                                            <th className="px-6 py-4 text-center text-[12px] font-bold text-black uppercase">Management</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                        {banners.map(banner => (
                                            <tr key={banner._id} className="group hover:bg-white/50 dark:hover:bg-slate-800/20 transition-all">
                                                <td className="px-6 py-4">
                                                    <div className="relative w-32 aspect-video rounded-md overflow-hidden bg-gray-50 border border-gray-100 shadow-sm transition-transform group-hover:scale-105">
                                                        {banner.type === 'image' ? (
                                                            <img src={getImageUrl(banner.url)} className="w-full h-full object-cover" alt="" />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center bg-[#05144b] text-white">
                                                                <i className="fa-solid fa-play text-xl opacity-50"></i>
                                                            </div>
                                                        )}
                                                        <div className="absolute top-2 left-2 px-1.5 py-0.5 rounded-sm bg-[#05144b]/40 backdrop-blur-md text-[8px] font-bold text-white uppercase border border-white/10">
                                                            {banner.type}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-black">
                                                    <div className="font-bold text-black line-clamp-1 group-hover:text-black/60 transition-colors tracking-tight text-[15px]">
                                                        {banner.title || 'Untitled Creative'}
                                                    </div>
                                                    <div className="text-[11px] text-black/40 mt-1 line-clamp-1 font-bold uppercase tracking-widest">
                                                        {banner.subtitle || 'No Tagline Defined'}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-50 text-[10px] font-bold text-black border border-gray-100">
                                                        {banner.order}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <button 
                                                        onClick={() => handleToggleActive(banner)}
                                                        className={`px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-widest transition-all border ${
                                                            banner.isActive 
                                                            ? 'bg-emerald-50 text-emerald-700 border-emerald-100' 
                                                            : 'bg-gray-50 text-black border-gray-100'
                                                        }`}
                                                    >
                                                        {banner.isActive ? (
                                                            <span className="flex items-center gap-1.5"><i className="fa-solid fa-circle text-[6px]"></i> Live</span>
                                                        ) : (
                                                            <span className="flex items-center gap-1.5"><i className="fa-regular fa-circle text-[6px]"></i> Stashed</span>
                                                        )}
                                                    </button>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <div className="flex items-center justify-center gap-2 opactiy-0 group-hover:opacity-100 transition-opacity">
                                                        <button 
                                                            onClick={() => handleEdit(banner)}
                                                            className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center shadow-sm"
                                                            title="Edit Detail"
                                                        >
                                                            <i className="fa-solid fa-pen-nib text-xs"></i>
                                                        </button>
                                                        <button 
                                                            onClick={() => handleDelete(banner._id)}
                                                            className="w-10 h-10 rounded-xl bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 hover:bg-rose-600 hover:text-white transition-all flex items-center justify-center shadow-sm"
                                                            title="Purge Asset"
                                                        >
                                                            <i className="fa-solid fa-trash-can text-xs"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    )
}


